import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Megaphone, Loader2, Languages } from 'lucide-react';
import { PROMO_BAR_TEMPLATES } from '../lib/promoBarTemplates';
import PromoBar from './PromoBar';
import {
  SITE_LANGUAGE_CODES,
  SITE_LANGUAGE_FLAGS,
  getSiteLanguageLabel,
  normalizeMessageTranslations,
  emptyMessageTranslations,
  isBrokenTranslation,
  isWrongLanguageForCode,
} from '../lib/siteLanguages';

export const DEFAULT_PROMO_BAR_CONFIG = {
  enabled: false,
  start_at: null,
  start_at_local: '',
  end_at: null,
  end_at_local: '',
  template: 'classic',
  message: 'Limited offer: use code {code} for {percent}% off your booking!',
  message_translations: emptyMessageTranslations(),
  coupon_code: '',
  discount_percent: 10,
  show_countdown: true,
  custom: {
    height_px: 44,
    background: 'linear-gradient(90deg, #0B1F3F 0%, #1e3a5f 100%)',
    border_css: 'none',
    text_color: '#FFFFFF',
    accent_color: '#38BDF8',
    font_size_px: 14,
  },
};

function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const s = String(iso).trim();
  const d = new Date(s.endsWith('Z') ? s : `${s}Z`);
  if (Number.isNaN(d.getTime())) return s.slice(0, 16).replace(' ', 'T');
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local.length === 16 ? `${local}:00` : local;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

export function mergePromoBarFromApi(data) {
  const base = {
    ...DEFAULT_PROMO_BAR_CONFIG,
    custom: { ...DEFAULT_PROMO_BAR_CONFIG.custom },
    message_translations: emptyMessageTranslations(),
  };
  if (!data || typeof data !== 'object') return base;
  const messageTranslations = normalizeMessageTranslations(
    data.message_translations,
    data.message || '',
  );
  return {
    ...base,
    ...data,
    custom: { ...base.custom, ...(data.custom || {}) },
    message_translations: messageTranslations,
    message:
      messageTranslations.en ||
      data.message ||
      base.message,
    start_at_local: isoToDatetimeLocal(data.start_at),
    end_at_local: isoToDatetimeLocal(data.end_at),
  };
}

export function promoBarPayload(form) {
  const { start_at_local, end_at_local, message_translations: _mt, ...rest } = form;
  const messageTranslations = normalizeMessageTranslations(
    form.message_translations,
    form.message || '',
  );
  const primaryMessage =
    messageTranslations.en ||
    form.message ||
    '';
  return {
    enabled: Boolean(form.enabled),
    template: rest.template || 'classic',
    message: primaryMessage,
    message_translations: messageTranslations,
    coupon_code: rest.coupon_code || '',
    discount_percent: Number(rest.discount_percent) || 0,
    show_countdown: Boolean(rest.show_countdown),
    custom: rest.custom || DEFAULT_PROMO_BAR_CONFIG.custom,
    start_at: datetimeLocalToIso(start_at_local),
    end_at: datetimeLocalToIso(end_at_local),
  };
}

const AdminPromoBarTab = ({ apiUrl, token, config, onChange }) => {
  const [saving, setSaving] = useState(false);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponsError, setCouponsError] = useState('');
  const [wowbookingSource, setWowbookingSource] = useState(null);
  const [previewOn, setPreviewOn] = useState(true);
  const [previewLang, setPreviewLang] = useState(SITE_LANGUAGE_CODES[0]);
  const [translatingLang, setTranslatingLang] = useState(null);
  const [translatingAll, setTranslatingAll] = useState(false);
  const [messageTab, setMessageTab] = useState('en');
  const [sourceLang, setSourceLang] = useState('en');

  const translationsSanitized = useMemo(
    () => normalizeMessageTranslations(config.message_translations, config.message),
    [config.message, config.message_translations],
  );

  const sourceText = useMemo(() => {
    const t = translationsSanitized[sourceLang];
    return t && !isBrokenTranslation(t) ? t : '';
  }, [translationsSanitized, sourceLang]);

  const setField = (key, value) => onChange({ ...config, [key]: value });
  const setCustom = (key, value) =>
    onChange({ ...config, custom: { ...config.custom, [key]: value } });

  useEffect(() => {
    const authToken = (localStorage.getItem('admin_token') || token || '').trim();
    if (!authToken) return undefined;

    let cancelled = false;
    const loadCoupons = async () => {
      setCouponsLoading(true);
      setCouponsError('');
      try {
        const res = await axios.get(`${apiUrl}/api/admin/promo-bar/coupons`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (cancelled) return;
        setActiveCoupons(Array.isArray(res.data?.coupons) ? res.data.coupons : []);
        setWowbookingSource(res.data?.source || null);
      } catch (err) {
        if (cancelled) return;
        setActiveCoupons([]);
        setWowbookingSource(null);
        setCouponsError(
          err.response?.data?.detail || 'Impossible de charger les coupons WowBooking',
        );
      } finally {
        if (!cancelled) setCouponsLoading(false);
      }
    };

    loadCoupons();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token]);

  const couponSelectValue = useMemo(() => {
    const code = (config.coupon_code || '').trim().toUpperCase();
    if (!code) return '';
    const match = activeCoupons.find((c) => c.code === code);
    return match ? match.code : '__custom__';
  }, [activeCoupons, config.coupon_code]);

  const handleCouponSelect = (value) => {
    if (!value) {
      onChange({ ...config, coupon_code: '', discount_percent: config.discount_percent ?? 10 });
      return;
    }
    if (value === '__custom__') return;
    const selected = activeCoupons.find((c) => c.code === value);
    if (!selected) return;
    onChange({
      ...config,
      coupon_code: selected.code,
      discount_percent: selected.discount_percent ?? config.discount_percent ?? 10,
    });
  };

  const setMessageForLang = (lang, value) => {
    const message_translations = normalizeMessageTranslations(
      {
        ...(config.message_translations || emptyMessageTranslations()),
        [lang]: value,
      },
      lang === 'en' ? value : config.message,
    );
    const message = message_translations.en || config.message;
    onChange({ ...config, message_translations, message });
    if (value.trim() && !isBrokenTranslation(value)) {
      setSourceLang(lang);
    }
  };

  const reloadFromApi = async (authToken) => {
    const res = await axios.get(`${apiUrl}/api/admin/promo-bar`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    onChange(mergePromoBarFromApi(res.data));
  };

  const pollUntilMessageUpdated = (authToken, lang, previousValue) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/admin/promo-bar`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const merged = mergePromoBarFromApi(res.data);
        const newValue = merged.message_translations?.[lang] || '';
        if (newValue && newValue !== previousValue) {
          clearInterval(interval);
          onChange(merged);
          if (isBrokenTranslation(newValue) || isWrongLanguageForCode(newValue, lang)) {
            toast.error(
              `Traduction refusée : le résultat n’est pas en ${getSiteLanguageLabel(lang)}. Réessayez.`,
            );
          } else {
            toast.success(`Message traduit en ${getSiteLanguageLabel(lang)}`);
          }
          setTranslatingLang(null);
          setTranslatingAll(false);
        }
      } catch {
        /* keep polling */
      }
    }, 3000);
    setTimeout(() => {
      clearInterval(interval);
      setTranslatingLang(null);
      setTranslatingAll(false);
    }, 360000);
  };

  const handleTranslateMessage = async (targetLang) => {
    const authToken = (localStorage.getItem('admin_token') || token || '').trim();
    if (!authToken) {
      toast.error('Session expirée — reconnectez-vous');
      return;
    }
    if (!sourceText.trim()) {
      toast.error('Renseignez d’abord le message dans la langue source');
      return;
    }
    if (targetLang === sourceLang) {
      toast.info(`La langue source est ${getSiteLanguageLabel(sourceLang)} — traduisez vers une autre langue`);
      return;
    }
    const previousValue = translationsSanitized[targetLang] || '';
    setTranslatingLang(targetLang);
    try {
      await axios.post(
        `${apiUrl}/api/admin/promo-bar/translate-message`,
        {
          target_lang: targetLang,
          source_lang: sourceLang,
          source_text: sourceText,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      toast.info('Traduction en cours…');
      pollUntilMessageUpdated(authToken, targetLang, previousValue);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec du lancement de la traduction');
      setTranslatingLang(null);
    }
  };

  const handleTranslateAll = async () => {
    const authToken = (localStorage.getItem('admin_token') || token || '').trim();
    if (!authToken) {
      toast.error('Session expirée — reconnectez-vous');
      return;
    }
    if (!sourceText.trim()) {
      toast.error('Renseignez d’abord le message dans la langue source');
      return;
    }
    setTranslatingAll(true);
    try {
      await axios.post(
        `${apiUrl}/api/admin/promo-bar/translate-all-messages`,
        { source_text: sourceText, source_lang: sourceLang },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      toast.info('Traductions en cours pour toutes les langues…');
      const interval = setInterval(async () => {
        try {
          await reloadFromApi(authToken);
        } catch {
          /* ignore */
        }
      }, 4000);
      setTimeout(() => {
        clearInterval(interval);
        setTranslatingAll(false);
        toast.success('Vérifiez les messages par langue ci-dessous');
      }, 120000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec du lancement des traductions');
      setTranslatingAll(false);
    }
  };

  const handleSave = async () => {
    const authToken = (localStorage.getItem('admin_token') || token || '').trim();
    if (!authToken) {
      toast.error('Session expirée — reconnectez-vous au back-office');
      return;
    }
    setSaving(true);
    try {
      const payload = promoBarPayload(config);
      const res = await axios.post(`${apiUrl}/api/admin/promo-bar`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      await reloadFromApi(authToken);
      if (res.data?.scheduled_active) {
        toast.success('Bandeau enregistré et visible sur le site vitrine');
      } else if (payload.enabled) {
        toast.warning(
          'Bandeau enregistré. Il ne s’affiche pas encore : vérifiez les dates de publication.',
        );
      } else {
        toast.warning(
          'Configuration enregistrée. Cochez « Activer le bandeau » puis enregistrez à nouveau.',
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de l’enregistrement du bandeau');
    } finally {
      setSaving(false);
    }
  };

  const previewConfig =
    previewOn && config.enabled ? promoBarPayload(config) : null;

  return (
    <>
      {previewOn && previewConfig ? (
        <PromoBar previewConfig={previewConfig} previewLanguage={previewLang} />
      ) : null}
      <Card className="bg-[#1A2332] border-[#2D3748]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            Bandeau promotionnel
          </CardTitle>
          <CardDescription className="text-gray-400">
            Messages multilingues ({SITE_LANGUAGE_CODES.join(', ')}). Variables : {'{code}'},{' '}
            {'{percent}'}, {'{discount}'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setField('enabled', e.target.checked)}
              className="w-4 h-4 rounded border-gray-600"
            />
            <span className="text-white font-medium">Activer le bandeau (respecte les dates ci-dessous)</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Début (optionnel)</Label>
              <Input
                type="datetime-local"
                value={config.start_at_local || ''}
                onChange={(e) => setField('start_at_local', e.target.value)}
                className="bg-[#0B1120] border-[#2D3748] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Fin (optionnel)</Label>
              <Input
                type="datetime-local"
                value={config.end_at_local || ''}
                onChange={(e) => setField('end_at_local', e.target.value)}
                className="bg-[#0B1120] border-[#2D3748] text-white"
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-[#2D3748] p-4 bg-[#0B1120]/40">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label className="text-white">Messages par langue</Label>
              <div className="flex items-center gap-2">
                <Label className="text-gray-400 text-xs whitespace-nowrap">Langue source :</Label>
                <select
                  value={sourceLang}
                  onChange={(e) => setSourceLang(e.target.value)}
                  className="bg-[#0B1120] border border-[#2D3748] text-white text-sm rounded px-2 py-1"
                >
                  {SITE_LANGUAGE_CODES.map((lang) => (
                    <option key={lang} value={lang}>
                      {SITE_LANGUAGE_FLAGS[lang]} {getSiteLanguageLabel(lang)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleTranslateAll}
                disabled={translatingAll || !!translatingLang}
                className="border-purple-500 text-purple-300 hover:bg-purple-500/10"
              >
                {translatingAll ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Languages className="w-3 h-3 mr-1" />
                )}
                Traduire toutes les langues
              </Button>
            </div>

            <Tabs value={messageTab} onValueChange={setMessageTab}>
              <TabsList className="bg-[#0F1729] border border-[#2D3748] flex flex-wrap h-auto">
                {SITE_LANGUAGE_CODES.map((lang) => (
                  <TabsTrigger
                    key={lang}
                    value={lang}
                    className="data-[state=active]:bg-[#2D3748] data-[state=active]:text-white text-gray-400"
                  >
                    {SITE_LANGUAGE_FLAGS[lang] || ''} {getSiteLanguageLabel(lang)}
                    {lang === sourceLang ? ' ●' : ''}
                  </TabsTrigger>
                ))}
              </TabsList>
              {SITE_LANGUAGE_CODES.map((lang) => (
                <TabsContent key={lang} value={lang} className="space-y-2 mt-3">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-gray-300">
                      Message {lang === sourceLang ? '(source)' : ''}
                    </Label>
                    {lang !== sourceLang ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleTranslateMessage(lang)}
                        disabled={translatingLang === lang || translatingAll}
                        className="border-purple-500 text-purple-300 hover:bg-purple-500/10 h-8"
                      >
                        {translatingLang === lang ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Traduction…
                          </>
                        ) : (
                          <>
                            <Languages className="w-3 h-3 mr-1" />
                            Traduire
                          </>
                        )}
                      </Button>
                    ) : null}
                  </div>
                  <textarea
                    value={translationsSanitized[lang] || ''}
                    onChange={(e) => setMessageForLang(lang, e.target.value)}
                    rows={3}
                    placeholder={`Message en ${getSiteLanguageLabel(lang)}…`}
                    className="w-full px-3 py-2 bg-[#0B1120] border border-[#2D3748] rounded-md text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </TabsContent>
              ))}
            </Tabs>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-white">Code promo (WowBooking)</Label>
              {wowbookingSource ? (
                <p className="text-xs text-gray-500">
                  Tenant actif : {wowbookingSource.tenant_slug || '—'} (
                  {wowbookingSource.mode || '—'})
                </p>
              ) : null}
              {couponsLoading ? (
                <p className="text-xs text-gray-400 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Chargement des coupons actifs…
                </p>
              ) : null}
              {couponsError ? (
                <p className="text-xs text-amber-400">{String(couponsError)}</p>
              ) : null}
              <select
                value={couponSelectValue}
                onChange={(e) => handleCouponSelect(e.target.value)}
                disabled={couponsLoading || activeCoupons.length === 0}
                className="w-full bg-[#0B1120] border border-[#2D3748] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Aucun code —</option>
                {activeCoupons.map((coupon) => (
                  <option key={coupon.id || coupon.code} value={coupon.code}>
                    {coupon.code} — {coupon.discount_percent}% (
                    {coupon.ends_at
                      ? `jusqu’au ${isoToDatetimeLocal(coupon.ends_at).replace('T', ' ')}`
                      : 'sans date de fin'}
                    )
                  </option>
                ))}
                {config.coupon_code &&
                !activeCoupons.some(
                  (c) => c.code === (config.coupon_code || '').trim().toUpperCase(),
                ) ? (
                  <option value="__custom__">
                    {(config.coupon_code || '').trim().toUpperCase()} (hors liste / expiré)
                  </option>
                ) : null}
              </select>
              {couponSelectValue === '__custom__' || activeCoupons.length === 0 ? (
                <Input
                  value={config.coupon_code || ''}
                  onChange={(e) => setField('coupon_code', e.target.value.toUpperCase())}
                  placeholder="Saisie manuelle du code"
                  className="bg-[#0B1120] border-[#2D3748] text-white font-mono"
                />
              ) : null}
              {!couponsLoading && !couponsError && activeCoupons.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Aucun coupon actif sur le tenant WowBooking configuré. Créez-en un dans
                  WowBooking ou vérifiez l’onglet WowBooking (mode dev / prod).
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label className="text-white">Remise (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={config.discount_percent ?? 10}
                onChange={(e) => setField('discount_percent', parseInt(e.target.value, 10) || 0)}
                className="bg-[#0B1120] border-[#2D3748] text-white"
              />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.show_countdown}
              onChange={(e) => setField('show_countdown', e.target.checked)}
              className="w-4 h-4 rounded border-gray-600"
            />
            <span className="text-gray-300">Afficher le compte à rebours jusqu&apos;à la date de fin</span>
          </label>

          <div className="space-y-3">
            <Label className="text-white">Modèle visuel</Label>
            <div className="flex flex-wrap gap-3">
              {Object.values(PROMO_BAR_TEMPLATES).map((tpl) => (
                <label key={tpl.id} className="flex items-center gap-2 cursor-pointer text-gray-300">
                  <input
                    type="radio"
                    name="promo_template"
                    checked={config.template === tpl.id}
                    onChange={() => setField('template', tpl.id)}
                  />
                  {tpl.label}
                </label>
              ))}
              <label className="flex items-center gap-2 cursor-pointer text-gray-300">
                <input
                  type="radio"
                  name="promo_template"
                  checked={config.template === 'custom'}
                  onChange={() => setField('template', 'custom')}
                />
                Personnalisé
              </label>
            </div>
          </div>

          {config.template === 'custom' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg border border-[#2D3748] bg-[#0B1120]/50">
              <div className="space-y-2">
                <Label className="text-white">Hauteur (px)</Label>
                <Input
                  type="number"
                  value={config.custom?.height_px ?? 44}
                  onChange={(e) => setCustom('height_px', parseInt(e.target.value, 10) || 44)}
                  className="bg-[#0B1120] border-[#2D3748] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Taille texte (px)</Label>
                <Input
                  type="number"
                  value={config.custom?.font_size_px ?? 14}
                  onChange={(e) => setCustom('font_size_px', parseInt(e.target.value, 10) || 14)}
                  className="bg-[#0B1120] border-[#2D3748] text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-white">Background (CSS)</Label>
                <Input
                  value={config.custom?.background || ''}
                  onChange={(e) => setCustom('background', e.target.value)}
                  className="bg-[#0B1120] border-[#2D3748] text-white font-mono text-sm"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-white">Bordure (CSS)</Label>
                <Input
                  value={config.custom?.border_css || 'none'}
                  onChange={(e) => setCustom('border_css', e.target.value)}
                  className="bg-[#0B1120] border-[#2D3748] text-white font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Couleur texte</Label>
                <Input
                  type="color"
                  value={config.custom?.text_color || '#FFFFFF'}
                  onChange={(e) => setCustom('text_color', e.target.value)}
                  className="h-10 w-full bg-[#0B1120] border-[#2D3748]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Couleur accent</Label>
                <Input
                  type="color"
                  value={config.custom?.accent_color || '#38BDF8'}
                  onChange={(e) => setCustom('accent_color', e.target.value)}
                  className="h-10 w-full bg-[#0B1120] border-[#2D3748]"
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={previewOn}
                onChange={(e) => setPreviewOn(e.target.checked)}
                disabled={!config.enabled}
                className="w-4 h-4 rounded border-gray-600 disabled:opacity-40"
              />
              <span className={config.enabled ? 'text-gray-300' : 'text-gray-500'}>
                Aperçu en direct
              </span>
            </label>
            {previewOn && config.enabled ? (
              <div className="flex items-center gap-2">
                <Label className="text-gray-400 text-sm">Langue aperçu</Label>
                <select
                  value={previewLang}
                  onChange={(e) => setPreviewLang(e.target.value)}
                  className="bg-[#0B1120] border border-[#2D3748] text-white text-sm rounded px-2 py-1"
                >
                  {SITE_LANGUAGE_CODES.map((lang) => (
                    <option key={lang} value={lang}>
                      {getSiteLanguageLabel(lang)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer le bandeau promo
          </Button>
        </CardContent>
      </Card>
    </>
  );
};

export default AdminPromoBarTab;
